// ---------------------------------------------------------------------------
// EDITABLE PAGE COPY — /logan-airport-car-rental-pickup-directions
// ---------------------------------------------------------------------------
// This file is the single source of truth for the WORDING on the Logan Airport
// pickup-directions page. Edit the text here anytime; the page layout in
// pages/LoganPickupDirections.jsx will not need to change.
//
// `icon` values are string keys mapped to lucide-react icons inside the page
// component (see ICONS in LoganPickupDirections.jsx). Add a new key there if
// you introduce a new icon name.
// ---------------------------------------------------------------------------

export const LOGAN_PICKUP = {
  // --- SEO / metadata ---
  meta: {
    title: "Logan Airport Car Rental Pickup Directions | Belo Rentals",
    description:
      "Flying into Boston Logan? Follow step-by-step car rental pickup and return directions for Belo Rentals, including shuttle and Economy Garage guidance.",
    path: "/logan-airport-car-rental-pickup-directions",
    keywords:
      "Logan Airport car rental pickup directions, Boston Logan car rental pickup, BOS car rental pickup, Logan Airport Economy Parking rental car pickup, Logan Airport car-share pickup, where to pick up a rental car at Logan Airport, Boston Airport rental car pickup directions",
  },

  breadcrumb: [
    { name: "Home", to: "/" },
    { name: "Logan Airport Car Rental", to: "/logan-airport-car-rental" },
    { name: "Pickup & Return Directions", to: "/logan-airport-car-rental-pickup-directions" },
  ],

  // --- Hero (above the fold) ---
  hero: {
    badge: "Boston Logan (BOS) travel guide",
    h1: "Logan Airport Car Rental Pickup & Return Directions",
    intro:
      "Just landed at Boston Logan? This step-by-step guide walks you from baggage claim to your Belo Rentals car and back again. Your pickup happens at the designated peer-to-peer car-share area on the ground level of the Logan Economy Parking Garage — not at the traditional airport Rental Car Center.",
    phoneLabel: "(978) 221-1451",
  },

  // --- Four-step overview ---
  fourStep: {
    heading: "Your pickup in four steps",
    subheading: "A quick overview before you land — the detailed directions are below.",
    steps: [
      { icon: "luggage", title: "Collect your bags", body: "Grab your luggage at baggage claim, then head down to the lower arrivals level of your terminal." },
      { icon: "signpost", title: "Find Ground Transportation", body: "Follow the 'Ground Transportation' signage to the on-airport shuttle bus stops." },
      { icon: "bus", title: "Ride Shuttle Route 88", body: "Board the Massport Airport Shuttle Route 88 toward Economy Parking and ride to the Economy Parking Garage." },
      { icon: "car", title: "Meet your car", body: "At the garage, go to the ground level — the peer-to-peer car-share pickup area. Your reservation has your exact space and access details." },
    ],
  },

  // --- Location clarification callout ---
  locationNote: {
    heading: "Two different locations — don't mix them up",
    body:
      "Logan has a traditional Rental Car Center used by the big counter brands. Belo Rentals does not pick up there. Your car is in the peer-to-peer car-share area on the ground level of the Economy Parking Garage, which is a separate location reached by Airport Shuttle Route 88 toward Economy Parking.",
  },

  // --- Per-terminal directions (tabs) ---
  terminals: {
    heading: "Directions by terminal",
    subheading:
      "The route is the same idea from every terminal: get to the lower arrivals level, follow Ground Transportation signs, and take Shuttle Route 88 toward Economy Parking.",
    items: [
      {
        key: "A",
        label: "Terminal A",
        title: "From Terminal A",
        body:
          "After you collect your bags, take the escalator or elevator down to the lower arrivals level. Follow the 'Ground Transportation' signage out to the on-airport shuttle stops and board Airport Shuttle Route 88 toward Economy Parking. Ride to the Economy Parking Garage, then make your way to the ground level, where the peer-to-peer car-share pickup area is located.",
      },
      {
        key: "B",
        label: "Terminal B",
        title: "From Terminal B",
        body:
          "Terminal B has more than one baggage-claim area, so once you have your luggage, head down to the lower arrivals level and look for the 'Ground Transportation' signs. They will lead you to the shuttle stops — board Airport Shuttle Route 88 toward Economy Parking. Get off at the Economy Parking Garage and go to the ground level to reach the car-share pickup area.",
      },
      {
        key: "C",
        label: "Terminal C",
        title: "From Terminal C",
        body:
          "From Terminal C baggage claim, continue down to the lower arrivals level and follow the 'Ground Transportation' signage to the on-airport shuttle stops. Board Airport Shuttle Route 88 toward Economy Parking and ride to the Economy Parking Garage. The peer-to-peer car-share pickup area is on the ground level of the garage.",
      },
      {
        key: "E",
        label: "Terminal E",
        title: "From Terminal E (International)",
        body:
          "If you're arriving on an international flight, clear customs and collect your luggage first. Then continue to the lower arrivals level and follow the 'Ground Transportation' signs to the shuttle stops. Board Airport Shuttle Route 88 toward Economy Parking, ride to the Economy Parking Garage, and head to the ground level to find the car-share pickup area.",
      },
    ],
  },

  // --- Pickup timeline ---
  pickupTimeline: {
    heading: "Pickup timeline",
    subheading: "What the journey looks like from the moment you land.",
    steps: [
      { title: "Land & deplane", body: "Arrive at your Logan terminal (A, B, C, or E) and make your way toward baggage claim." },
      { title: "Baggage claim", body: "Collect your checked luggage, then head to the lower arrivals level." },
      { title: "Ground Transportation", body: "Follow the 'Ground Transportation' signage to the on-airport shuttle bus stops." },
      { title: "Shuttle Route 88", body: "Board Airport Shuttle Route 88 toward Economy Parking. Travel time varies with traffic and time of day, so leave yourself a comfortable buffer." },
      { title: "Economy Parking Garage", body: "Exit the shuttle at the Economy Parking Garage and go to the ground level." },
      { title: "Your car", body: "Follow the exact space and access instructions in your reservation to find and unlock your car — then you're on the road." },
    ],
  },

  // --- Return timeline ---
  returnTimeline: {
    heading: "Return timeline",
    subheading: "Returning your car and getting to your departure terminal.",
    steps: [
      { title: "Plan your buffer", body: "Give yourself extra time before your flight for the return, the shuttle ride, and airport security." },
      { title: "Fuel & belongings", body: "Follow any fuel guidance in your reservation and double-check the car for personal items." },
      { title: "Return to the drop-off spot", body: "Bring the car back to the location and space described in your reservation on the ground level of the Economy Parking Garage." },
      { title: "Complete the return steps", body: "Finish the return exactly as your reservation instructs — where to leave the keys, how to lock up, and any check-in photos are all reservation-specific." },
      { title: "Shuttle to your terminal", body: "From the garage, board the on-airport shuttle back toward the terminals and ride to your departure terminal." },
      { title: "Check in & fly", body: "Head to your airline's departures level, check in, and enjoy your flight." },
    ],
  },

  // --- Map ---
  map: {
    heading: "Where the pickup area is",
    // Google Maps embed — no API key required. Centered on the Economy Parking Garage.
    embedSrc: "https://www.google.com/maps?q=Boston%20Logan%20Economy%20Parking%20Garage&output=embed",
    caption: "Logan Airport Economy Parking Garage — car-share pickup area (ground level)",
    note:
      "This map shows the general Economy Parking Garage area. It is a shuttle ride from the terminals via Airport Shuttle Route 88 toward Economy Parking. Your exact vehicle location, parking space, and access method are provided in your reservation.",
  },

  // --- Already booked notice ---
  alreadyBooked: {
    heading: "Already booked? Check your reservation",
    body:
      "Every Belo Rentals booking includes the specifics you'll need: the exact vehicle location and parking space, how to access the car, key instructions, and how to return it. These details are reservation-specific, so please follow the instructions in your booking rather than guessing. If anything is unclear, reach us at (978) 221-1451.",
  },

  // --- Common mistakes ---
  commonMistakes: {
    heading: "Common mistakes to avoid",
    items: [
      "Going to the traditional Rental Car Center. That's for the counter brands — your Belo car is at the Economy Parking Garage car-share area, a different location.",
      "Waiting at the terminal curb for a handoff. Belo Rentals does not provide terminal-curbside vehicle pickup at this location — you'll ride the shuttle to Economy Parking.",
      "Boarding the wrong shuttle. Look specifically for Airport Shuttle Route 88 toward Economy Parking.",
      "Not reading your reservation before you land. Your exact space, access method, and key instructions are in the booking — screenshot them in case of spotty signal.",
      "Wandering the garage looking for your car. Don't search randomly; your reservation tells you exactly where it is and how to access it.",
    ],
  },

  // --- Travel tips ---
  travelTips: {
    heading: "Smart travel tips",
    items: [
      { icon: "smartphone", title: "Save your details offline", body: "Screenshot your reservation's location, space, and access instructions before you land in case airport signal is weak." },
      { icon: "clock", title: "Leave a buffer", body: "Shuttle timing varies with traffic and time of day, so build in extra minutes both at pickup and return." },
      { icon: "idcard", title: "Have your essentials ready", body: "Keep your driver's license and payment method handy for a smooth start." },
      { icon: "messages", title: "Message through your booking", body: "If your flight is delayed or plans change, use your reservation to stay in touch." },
      { icon: "mappin", title: "Note where you park on return", body: "Jot down the level and row so the return goes quickly when it's time to fly home." },
    ],
  },

  // --- FAQ (must match FAQPage JSON-LD) ---
  faqs: [
    {
      q: "Where do I pick up my Belo Rentals car at Logan Airport?",
      a: "Your car is in the designated peer-to-peer car-share area on the ground level of the Boston Logan Economy Parking Garage. This is separate from the traditional airport Rental Car Center. Your exact parking space and access method are provided in your reservation.",
    },
    {
      q: "How do I get to Economy Parking from my terminal?",
      a: "From baggage claim, head down to the lower arrivals level and follow the 'Ground Transportation' signage to the on-airport shuttle stops. Board Airport Shuttle Route 88 toward Economy Parking and ride to the Economy Parking Garage.",
    },
    {
      q: "Is the Economy Parking car-share area the same as the Rental Car Center?",
      a: "No. The Logan Airport Rental Car Center and the Economy Parking peer-to-peer car-share pickup area are different locations. Belo Rentals cars are picked up at the Economy Parking Garage, not the Rental Car Center.",
    },
    {
      q: "Does Belo Rentals meet me at the terminal curb?",
      a: "No. Belo Rentals does not provide terminal-curbside vehicle pickup at this location. You'll take Airport Shuttle Route 88 toward Economy Parking and meet your car at the garage. If you'd prefer an airport handoff, start from our main Logan Airport car rental page.",
    },
    {
      q: "How long does the shuttle take to Economy Parking?",
      a: "Shuttle travel time varies with traffic and time of day, so we don't quote an exact figure. Please leave yourself a comfortable buffer for both pickup and return.",
    },
    {
      q: "What if I can't find my car in the garage?",
      a: "Don't search the garage randomly. Your reservation includes the exact vehicle location, parking space, and access instructions — follow those. If you still need help, call us at (978) 221-1451.",
    },
    {
      q: "Where do I return the car?",
      a: "Return details are reservation-specific. Bring the car back to the location and space described in your booking on the ground level of the Economy Parking Garage, and complete the return steps exactly as your reservation instructs.",
    },
  ],

  finalCta: {
    heading: "Ready to book your Logan Airport rental?",
    body: "Reserve a clean, well-maintained car and get clear, reservation-specific pickup instructions before you fly.",
  },
};
